import { callFirebaseFunction } from 'boot/firebase';
import { getDownloadURL } from 'firebase/storage';
import { chain, omit } from 'lodash';
import { useTime } from 'src/composables/time';
import brandColors from 'src/constants/brandColors';
import { CLIENT_RECOVERY_WINDOW_DAYS } from 'src/constants/clients';
import TaskListsDB from 'src/fb/taskLists-db';
import { auth as useAuthStore } from 'src/store/auth';
import { clients as useClientsStore } from 'src/store/clients';
import { firebaseUploader as useFirebaseUploaderStore } from 'src/store/firebaseUploader';
import { network as useNetworkStore } from 'src/store/network';
import { sessions as useSessionsStore } from 'src/store/sessions';
import { getClientInitials } from 'src/utils/AppUtils';
import { computed } from 'vue';

// eslint-disable-next-line import/prefer-default-export
export function useClients() {
  const authStore = useAuthStore();
  const clientsStore = useClientsStore();
  const firebaseUploaderStore = useFirebaseUploaderStore();
  const networkStore = useNetworkStore();
  const sessionsStore = useSessionsStore();
  const { getDateDiff } = useTime();

  const connectedClinic = computed(() => authStore.connectedClinic);
  const billing = computed(() => authStore.billing);
  const offlineMode = computed(() => networkStore.offlineMode);
  const clients = computed(() => clientsStore.clients);

  /**
   * Returns a random index from a list of brand colors
   * @returns Number
   */
  const getRandomColor = () => {
    return Math.floor(Math.random() * brandColors.length);
  }; // getRandomColor()

  /**
   * Returns a client's profile picture path or a default avatar based on the client gender
   * @param {Object} client A Client object
   *
   * @returns String The path to the proper image
   */
  const getClientProfileImagePath = (client, alwaysGetInitials = false) => {
    if (client.photo && !offlineMode.value) {
      return client.photo;
    }

    const textColor = '#fff';
    const fallbackBgColor = 'c3c3c3';
    const avatarBgColor = brandColors[client.avatarBgColor] || fallbackBgColor;

    const initials = alwaysGetInitials
      ? getClientInitials(client.clientName)
      : client.avatarInitials || getClientInitials(client.clientName);

    const style = `
      color: ${textColor};
      line-height: 1;
      font-family:
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        'Roboto',
        'Oxygen',
        'Ubuntu',
        'Fira Sans',
        'Droid Sans',
        'Helvetica Neue',
        sans-serif;
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="128px" height="128px" viewBox="0 0 128 128" version="1.1">
        <rect fill="#${avatarBgColor}" width="128" height="128"/>
        <text
          x="50%" y="50%"
          style="${style}"
          alignment-baseline="middle"
          text-anchor="middle"
          font-size="64"
          font-weight="400"
          dy=".1em"
          dominant-baseline="middle"
          fill="${textColor}"
        >
          ${initials}
        </text>
      </svg>`)}`;
  }; // getClientProfileImagePath()

  const saveStaffPhoto = async (clientId, selectedStaffPhoto) => {
    if (!selectedStaffPhoto || !clientId) {
      return;
    }

    const extension = chain(selectedStaffPhoto)
      .get('name')
      .split('.')
      .last()
      .value();

    // Save client photo
    const fileInfo = {
      destinationPath: `/clients/${connectedClinic.value.id}/${clientId}/avatar/`,
      destinationFilename: `photo.${extension}`,
      file: selectedStaffPhoto,
      metadata: {
        customMetadata: {
          clientPhoto: true,
        },
      },
      callback: async (fileRef) => {
        const clientInfo = {
          id: clientId,
          photo: await getDownloadURL(fileRef),
        };

        await clientsStore.updateClient(clientInfo);
      },
    };

    await firebaseUploaderStore.addToUploadQueue(fileInfo);
  }; // saveStaffPhoto()

  const saveTaskLists = (staffMemberId, taskLists) => {
    const taskListActions = chain(taskLists)
      .reject((taskList) => !['create', 'update', 'delete'].includes(taskList.editStatus))
      .map((taskList) => {
        const taskListsDb = new TaskListsDB(staffMemberId);
        switch (taskList.editStatus) {
          case 'create':
            return taskListsDb.create(omit(taskList, 'editStatus'));
          case 'update':
            return taskListsDb.update(omit(taskList, 'editStatus'));
          case 'delete':
            return taskListsDb.delete(taskList.id);
          default:
            return null;
        }
      }).value();
    return Promise.all(taskListActions);
  }; // saveTaskLists()

  /**
   @typedef EditStaffMemberInfo
   @type {Object}
   @property {Object} editStaffMember
   @property {Boolean} licenseNeeded
   @property {Array<Object>} taskLists
   @property {Object} selectedStaffPhoto
   @property {Boolean} hasStaffMember
   */

  /**
   * Creates new staffMember, updates photo, subscription and taskLists
   * @param {EditStaffMemberInfo} editStaffMemberInfo Information required while staffMember creation
   * @returns String Id of created client
   */
  const createNewStaffMember = async ({ editStaffMember, licenseNeeded, taskLists, selectedStaffPhoto }) => {
    const { id } = await clientsStore.createClient(editStaffMember);
    await saveStaffPhoto(id, selectedStaffPhoto);

    await callFirebaseFunction('updateSubscriptionUsage', {
      clinicId: connectedClinic.value.id,
      adjustLicenses: licenseNeeded,
      initStaff: !billing.value.subscriptionLicenses?.staff?.price,
    });

    await saveTaskLists(id, taskLists);
    return id;
  }; // createNewStaffMember()

  const getUserClientAssociations = (user, staffMemberInfo, forceUpdate = false) => {
    const { associatedClients, editStaffMember, hasActiveStaffMember } = staffMemberInfo;

    const selectedClients = staffMemberInfo.returnUnitClients
      ? staffMemberInfo.selectedClients
      : staffMemberInfo.selectedClients.filter((client) => !staffMemberInfo.clientsByUnits.includes(client.id));

    const forceUpdateEditStatus = forceUpdate ? { editStatus: 'update' } : {};

    // Add associations to clients that are selected
    const clientAssociations = selectedClients.map((client) => ({
      client: client.id,
      user: user.userId || null,
      userName: user.name,
      role: user.role,
      userPrivilege: user.userPrivilege || null,
      ...(associatedClients.includes(client.id) ? forceUpdateEditStatus : { editStatus: 'create' }),
    }));

    // Delete associations to clients that are not selected but were previously associated
    const deletedAssociations = associatedClients
      .filter((clientId) => !selectedClients.find((client) => client.id === clientId))
      // Never delete the user's own staff-profile link: it is (re)created below in the
      // hasActiveStaffMember block, so queuing a delete for it here is contradictory and
      // makes the final self-link state depend on Promise.all ordering. The active user must
      // always keep access to their own profile (see also the self-link guard in the backend
      // trigger updateUsersClientsByUnits).
      .filter((clientId) => !(hasActiveStaffMember && clientId === editStaffMember.id))
      .map((clientId) => ({
        client: clientId,
        user: user.userId || null,
        editStatus: 'delete',
      }));

    const userAssociations = clientAssociations.concat(deletedAssociations);

    if (hasActiveStaffMember) {
      // Provision access to own profile
      userAssociations.push({
        client: editStaffMember.id,
        user: user.userId || null,
        role: user.role,
        userName: user.name,
        editStatus: 'create',
      });
      // Add associations to staff member supervisors
      userAssociations.push(
        ...staffMemberInfo.staffMemberSupervisors.map((staffMemberSupervisor) => ({
          ...staffMemberSupervisor,
          client: editStaffMember.id,
        })),
      );
    }

    return userAssociations;
  }; // getUserClientAssociations()

  const currentClient = computed(() => authStore.currentClient);

  /**
   * Gets the client object associated with the session
   * @param {string} clientId - The client ID to search for
   * @returns {Object|null} The client object if found, otherwise null
   */
  const getSessionClient = (clientId) => {
    if (currentClient.value?.id === clientId) {
      return currentClient.value;
    }

    return clients.value.find(({ id }) => id === clientId) || null;
  }; // getSessionClient()

  const activeSession = computed(() => sessionsStore.activeSession);

  const activeSessionClient = computed(() => {
    const clientId = activeSession.value?.client;
    if (!clientId) {
      return null;
    }

    if (currentClient.value?.id === clientId) {
      return currentClient.value;
    }

    return clients.value.find(({ id }) => id === clientId) || null;
  });

  /**
   * @param {Date|null} deletedOn
   * @returns {number|null} whole days since the delete, or null when the date is unknown
   */
  const getDaysSinceDeleted = (deletedOn) => (deletedOn ? getDateDiff(Date.now(), deletedOn, 'days') : null);

  /**
   * @param {Date|null} deletedOn
   * @returns {number} days left to restore the client; 0 once the window is over or the date is unknown
   */
  const getRecoveryDaysRemaining = (deletedOn) => {
    const daysSinceDeleted = getDaysSinceDeleted(deletedOn);
    return daysSinceDeleted === null ? 0 : Math.max(CLIENT_RECOVERY_WINDOW_DAYS - daysSinceDeleted, 0);
  }; // getRecoveryDaysRemaining()

  /**
   * @param {Date|null} deletedOn
   * @returns {boolean}
   */
  const isRestorable = (deletedOn) => getRecoveryDaysRemaining(deletedOn) > 0;

  return {
    activeSessionClient,
    createNewStaffMember,
    getClientProfileImagePath,
    getDaysSinceDeleted,
    getRandomColor,
    getRecoveryDaysRemaining,
    getSessionClient,
    getUserClientAssociations,
    isRestorable,
    saveStaffPhoto,
    saveTaskLists,
  };
}
